/**
 * Small status glyphs drawn as SVG so they read at 14px. Work in progress
 * follows the moon (dayax): new moon, crescent, half, gibbous, full.
 */
import { HEALTH } from "@/lib/format";
import { useRow } from "@/lib/store";
import type { Health, Id, IdeaStatus, InitiativeStatus, ProjectStatus, StateCategory } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";

export function PriorityIcon({ priority, className }: { priority: number; className?: string }) {
  if (priority === 1) {
    return (
      <svg viewBox="0 0 16 16" className={cn("size-4 shrink-0", className)} aria-label="Urgent">
        <rect x="1.5" y="1.5" width="13" height="13" rx="3" fill="#F2994A" />
        <rect x="7.1" y="4" width="1.8" height="5.2" rx="0.9" fill="white" />
        <circle cx="8" cy="11.3" r="1" fill="white" />
      </svg>
    );
  }
  if (priority === 0) {
    return (
      <svg viewBox="0 0 16 16" className={cn("size-4 shrink-0 text-muted-foreground", className)} aria-label="No priority">
        {[3, 7, 11].map((x) => (
          <rect key={x} x={x} y="7.25" width="2.2" height="1.5" rx="0.5" fill="currentColor" opacity="0.7" />
        ))}
      </svg>
    );
  }
  const filled = priority === 2 ? 3 : priority === 3 ? 2 : 1;
  return (
    <svg viewBox="0 0 16 16" className={cn("size-4 shrink-0 text-foreground/80", className)} aria-label={["", "", "High", "Medium", "Low"][priority]}>
      {[0, 1, 2].map((i) => (
        <rect key={i} x={2.5 + i * 4} y={10 - i * 3.2} width="2.6" height={4 + i * 3.2} rx="0.8" fill="currentColor" opacity={i < filled ? 1 : 0.28} />
      ))}
    </svg>
  );
}

export function StateIcon({ category, color, progress = 0.5, className }: { category: StateCategory; color: string; progress?: number; className?: string }) {
  const c = cn("size-[15px] shrink-0", className);
  switch (category) {
    case "triage":
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="Triage">
          <circle cx="8" cy="8" r="6.5" fill="none" stroke={color} strokeWidth="1.6" />
          <path d="M5 8h6M8.5 5.5 11 8l-2.5 2.5" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "backlog":
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="Backlog">
          <circle cx="8" cy="8" r="6.3" fill="none" stroke={color} strokeWidth="1.6" strokeDasharray="2.2 2" />
        </svg>
      );
    case "unstarted":
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="Not started">
          <circle cx="8" cy="8" r="6.3" fill="none" stroke={color} strokeWidth="1.6" />
        </svg>
      );
    case "started": {
      // Lit from the right; the terminator is an ellipse whose width shrinks
      // to a straight line at half and bulges the other way past it.
      const r = 3.6;
      const p = Math.max(0.15, Math.min(0.85, progress));
      const rx = Math.abs(1 - 2 * p) * r;
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="In progress">
          <circle cx="8" cy="8" r="6.3" fill="none" stroke={color} strokeWidth="1.6" />
          <path d={`M8 ${8 - r} A${r} ${r} 0 0 1 8 ${8 + r} A${rx} ${r} 0 0 ${p > 0.5 ? 1 : 0} 8 ${8 - r} Z`} fill={color} />
        </svg>
      );
    }
    case "completed":
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="Done">
          <circle cx="8" cy="8" r="7" fill={color} />
          <path d="M5 8.2 7.1 10.3 11 6" stroke="white" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "canceled":
      return (
        <svg viewBox="0 0 16 16" className={c} aria-label="Canceled">
          <circle cx="8" cy="8" r="7" fill={color} />
          <path d="M5.7 5.7 10.3 10.3M10.3 5.7 5.7 10.3" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      );
  }
}

/** Status glyph for a workflow state id, with "started" progress by position. */
export function StateIconFor({ stateId, className }: { stateId: Id | null | undefined; className?: string }) {
  const s = useRow("workflowState", stateId);
  const changed = useChanged(stateId);
  if (!s) return <span className={cn("size-[15px] shrink-0", className)} />;
  return (
    <span className={cn("relative inline-flex shrink-0", changed && "animate-pop")} key={changed ? stateId ?? "" : "still"}>
      <StateIcon category={s.category} color={s.color} progress={s.name.toLowerCase().includes("review") ? 0.75 : 0.5} className={className} />
      {changed && s.category === "completed" && <span aria-hidden className="pointer-events-none absolute inset-0 animate-ring rounded-full border-2" style={{ borderColor: s.color }} />}
    </span>
  );
}

/** True for a moment after `value` changes (not on first render). */
function useChanged<T>(value: T, ms = 700) {
  const first = useRef(value);
  const [changed, setChanged] = useState(false);
  useEffect(() => {
    if (Object.is(first.current, value)) return;
    first.current = value;
    setChanged(true);
    const t = setTimeout(() => setChanged(false), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return changed;
}

export const PROJECT_STATUS: Record<ProjectStatus, { label: string; color: string; category: StateCategory }> = {
  backlog: { label: "Backlog", color: "#A1A7B3", category: "backlog" },
  planned: { label: "Planned", color: "#8B95A5", category: "unstarted" },
  started: { label: "In progress", color: "#E8B634", category: "started" },
  paused: { label: "Paused", color: "#A1A7B3", category: "started" },
  completed: { label: "Completed", color: "#5E6AD2", category: "completed" },
  canceled: { label: "Canceled", color: "#9AA0A6", category: "canceled" },
};

export function ProjectStatusIcon({ status, className }: { status: ProjectStatus; className?: string }) {
  const s = PROJECT_STATUS[status];
  return <StateIcon category={s.category} color={s.color} progress={status === "paused" ? 0.3 : 0.5} className={className} />;
}

export const INITIATIVE_STATUS: Record<InitiativeStatus, { label: string; color: string; category: StateCategory }> = {
  proposed: { label: "Proposed", color: "#A1A7B3", category: "backlog" },
  active: { label: "Active", color: "#2F9E6E", category: "started" },
  completed: { label: "Completed", color: "#5E6AD2", category: "completed" },
  canceled: { label: "Canceled", color: "#9AA0A6", category: "canceled" },
};

export const IDEA_STATUS: Record<IdeaStatus, { label: string; color: string; description: string }> = {
  inbox: { label: "Inbox", color: "#8B95A5", description: "New, not looked at yet" },
  exploring: { label: "Exploring", color: "#4F9CF0", description: "Gathering evidence and talking to people" },
  shaped: { label: "Shaped", color: "#E8B634", description: "Problem and bet are clear; ready to decide" },
  promoted: { label: "Promoted", color: "#2F9E6E", description: "Became a project or initiative" },
  parked: { label: "Parked", color: "#A1A7B3", description: "Not now; comes back on its revisit date" },
  rejected: { label: "Rejected", color: "#9AA0A6", description: "Decided against" },
};

export function IdeaStatusDot({ status, className }: { status: IdeaStatus; className?: string }) {
  return <span className={cn("inline-block size-2.5 shrink-0 rounded-full", className)} style={{ background: IDEA_STATUS[status].color }} />;
}

export function HealthBadge({ health, className }: { health: Health | null; className?: string }) {
  if (!health) return <span className={cn("text-xs text-muted-foreground", className)}>No updates</span>;
  const h = HEALTH[health];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", className)} style={{ color: h.color }}>
      <span className="size-2 rounded-full" style={{ background: h.color }} />
      {h.label}
    </span>
  );
}

export function ColorDot({ color, className }: { color: string; className?: string }) {
  return <span className={cn("inline-block size-2 shrink-0 rounded-full", className)} style={{ background: color }} />;
}

export function TeamIcon({ teamId, className }: { teamId: Id | null | undefined; className?: string }) {
  const t = useRow("team", teamId);
  if (!t) return null;
  return (
    <span className={cn("inline-flex size-[18px] shrink-0 items-center justify-center rounded text-[9px] font-bold text-white", className)} style={{ background: t.color }} title={t.name}>
      {t.icon || t.key.slice(0, 2)}
    </span>
  );
}

/** Progress as a moon: an outline when nothing is done, waxing to full at 100%. */
export function ProgressRing({ value, size = 16, color = "currentColor" }: { value: number; size?: number; color?: string }) {
  const p = Math.max(0, Math.min(1, value));
  const c = size / 2;
  const r = size / 2 - 3.2;
  const rx = Math.abs(1 - 2 * p) * r;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="shrink-0" aria-label={`${Math.round(p * 100)}%`}>
      <circle cx={c} cy={c} r={size / 2 - 0.9} fill="none" stroke={color} strokeOpacity={p >= 1 ? 1 : 0.45} strokeWidth="1.4" />
      {p >= 1 ? <circle cx={c} cy={c} r={r} fill={color} /> : p > 0 && <path d={`M${c} ${c - r} A${r} ${r} 0 0 1 ${c} ${c + r} A${rx} ${r} 0 0 ${p > 0.5 ? 1 : 0} ${c} ${c - r} Z`} fill={color} />}
    </svg>
  );
}
